import { useEffect, useState, createContext, useContext } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import { cn } from "../../lib/utils";

const DESKTOP_COLLAPSED_WIDTH = 68;
const DESKTOP_EXPANDED_WIDTH = 240;
const RAIL_PAD_X = 14;

const SidebarContext = createContext(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}) => {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({ children, open, setOpen, animate }) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};

export const SidebarBody = ({ className, ...props }) => {
  return (
    <>
      <DesktopSidebar className={className} {...props} />
      <MobileSidebar className={className} {...props} />
    </>
  );
};

const DesktopShell = styled(motion.div)`
  height: 100%;
  max-height: 100dvh;
  padding: 18px ${RAIL_PAD_X}px;
  display: none;
  flex-direction: column;
  background: ${({ theme }) => theme.surface || "#1f2937"};
  width: ${DESKTOP_EXPANDED_WIDTH}px;
  flex-shrink: 0;
  border-radius: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.35);
  position: relative;
  box-sizing: border-box;

  @media (min-width: 1025px) {
    display: flex;
  }
`;

const DesktopSidebar = ({ className, children, ...props }) => {
  const { open, setOpen, animate } = useSidebar();
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const applySidebarOffset = () => {
      if (window.innerWidth <= 1024) {
        root.style.removeProperty("--sidebar-offset");
        return;
      }
      root.style.setProperty(
        "--sidebar-offset",
        open
          ? `${DESKTOP_EXPANDED_WIDTH}px`
          : `${DESKTOP_COLLAPSED_WIDTH}px`,
      );
    };

    // Apply immediately and on breakpoint changes.
    applySidebarOffset();
    window.addEventListener("resize", applySidebarOffset);
    return () => window.removeEventListener("resize", applySidebarOffset);
  }, [open]);

  return (
    <DesktopShell
      className={cn(className)}
      animate={{
        width: animate
          ? open
            ? `${DESKTOP_EXPANDED_WIDTH}px`
            : `${DESKTOP_COLLAPSED_WIDTH}px`
          : `${DESKTOP_EXPANDED_WIDTH}px`,
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      {...props}
    >
      {children}
    </DesktopShell>
  );
};

const MobileBar = styled.div`
  height: 2.5rem;
  padding: 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  background: transparent;
  width: 100%;

  @media (min-width: 1025px) {
    display: none;
  }

  /* Keep this strip from blocking content taps except for explicit controls. */
  pointer-events: none;
`;

const MobilePanel = styled(motion.div)`
  position: fixed;
  inset: 0;
  height: 100dvh;
  max-height: 100dvh;
  width: 100%;
  background: ${({ theme }) => theme.surface || theme.bg || "#fff"};
  padding: 2rem 1.5rem;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  box-sizing: border-box;
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  /* Scrolling the menu must not chain into the page underneath. */
  overscroll-behavior: contain;
`;

// Round menu/close control; sized in px on phones so the global 44px/40px min-height can't stretch it into an oval.
const MenuToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  /* The floating one sits over scrolling content, so it needs an opaque fill. */
  background: ${({ $floating }) => ($floating ? "var(--surface)" : "transparent")};
  color: inherit;
  cursor: pointer;
  padding: 0;
  pointer-events: auto;

  @media (max-width: 450px) {
    width: 44px;
    height: 44px;
  }

  @media (max-width: 390px) {
    width: 40px;
    height: 40px;
  }
`;

const MobileSidebar = ({ className, children, ...props }) => {
  const { open, setOpen } = useSidebar();
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 1024 : true,
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncViewport = () => setIsMobileViewport(window.innerWidth <= 1024);
    syncViewport();
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  const handleToggle = () => {
    if (!isMobileViewport) return;
    const next = !open;
    setOpen(next);
    if (next && typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  };
  return (
    <>
      <MobileBar className={cn(className)} {...props}>
        <MenuToggle
          type="button"
          onClick={handleToggle}
          $floating
          aria-label={open ? "Close sidebar" : "Open sidebar"}
        >
          <Menu />
        </MenuToggle>
        {isMobileViewport && (
          <MobilePanel
            $open={open}
            initial={false}
            animate={{ x: open ? 0 : "-100%", opacity: open ? 1 : 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={cn(className)}
            // inert, not aria-hidden: it also drops focus from the Close button that just closed the
            // panel, which aria-hidden on a focused element's ancestor is not allowed to do.
            inert={open ? undefined : ""}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "1.5rem",
              }}
            >
              <MenuToggle
                type="button"
                onClick={handleToggle}
                aria-label="Close sidebar"
              >
                <X />
              </MenuToggle>
            </div>
            {children}
          </MobilePanel>
        )}
      </MobileBar>
    </>
  );
};

const linkRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 12px 10px;
  margin: 0 -6px;
  width: calc(100% + 12px);
  box-sizing: border-box;
  color: ${({ theme }) => theme.ink || "rgba(255,255,255,0.92)"};
  border-radius: 14px;
  user-select: none;

  &:hover {
    background: ${({ theme }) =>
      theme.bg === "#10182a"
        ? theme.surface || "rgba(255,255,255,0.08)"
        : theme.bg || "rgba(0,0,0,0.06)"};
  }

  &:active {
    background: ${({ theme }) =>
      theme.bg === "#10182a"
        ? theme.surface || "rgba(255,255,255,0.12)"
        : theme.bg || "rgba(0,0,0,0.08)"};
  }

  & svg {
    color: ${({ theme }) => theme.ink || "rgba(255,255,255,0.9)"};
  }
`;

/* NavLink adds class "active" when className is a string (styled-components forwards a string). */
const activeLinkStyles = css`
  &.active {
    background: ${({ theme }) =>
      theme.bg === "#10182a"
        ? theme.surface || "rgba(255,255,255,0.08)"
        : theme.bg || "rgba(0,0,0,0.06)"};
  }

  &.active:hover {
    background: ${({ theme }) =>
      theme.bg === "#10182a"
        ? theme.surface || "rgba(255,255,255,0.1)"
        : theme.bg || "rgba(0,0,0,0.08)"};
  }

  &.active:active {
    background: ${({ theme }) =>
      theme.bg === "#10182a"
        ? theme.surface || "rgba(255,255,255,0.12)"
        : theme.bg || "rgba(0,0,0,0.08)"};
  }
`;

const SidebarAnchorNav = styled(NavLink)`
  text-decoration: none;
  ${linkRowStyles}
  ${activeLinkStyles}
`;

const SidebarAnchorPlain = styled(Link)`
  text-decoration: none;
  ${linkRowStyles}
  ${activeLinkStyles}
`;

const SidebarActionEl = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-align: left;
  width: 100%;
  ${linkRowStyles}
`;

/* Wraps the icon so the notification dot can anchor to its top-right corner.
   Anchoring to the icon (not the row) keeps the dot visible when the rail is
   collapsed and only icons are shown. */
const IconSlot = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NotificationDot = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #ef4444;
  border: 2px solid ${({ theme }) => theme.surface || "#1f2937"};
  box-sizing: content-box;
  pointer-events: none;
`;

export const SidebarLink = ({ link, className, ...props }) => {
  const { open, animate } = useSidebar();
  const location = useLocation();
  const useCustomActive = typeof link.activeMatch === "function";
  const customActive = useCustomActive ? link.activeMatch(location) : false;

  const icon = (
    <IconSlot>
      {link.icon}
      {link.badge ? <NotificationDot aria-hidden /> : null}
    </IconSlot>
  );

  const label = (
    <motion.span
      animate={{
        display: animate ? (open ? "inline-block" : "none") : "inline-block",
        opacity: animate ? (open ? 1 : 0) : 1,
      }}
      style={{
        fontSize: "0.875rem",
        whiteSpace: "nowrap",
        color: "inherit",
        transition: "transform 150ms",
      }}
    >
      {link.label}
    </motion.span>
  );

  // Optional right-aligned slot (e.g. a gear beside the profile name); hidden with the label when collapsed.
  const trailing = link.trailing ? (
    <motion.span
      animate={{
        display: animate ? (open ? "inline-flex" : "none") : "inline-flex",
        opacity: animate ? (open ? 1 : 0) : 1,
      }}
      style={{ marginLeft: "auto", alignItems: "center", flexShrink: 0 }}
    >
      {link.trailing}
    </motion.span>
  ) : null;

  if (useCustomActive) {
    return (
      <SidebarAnchorPlain
        to={link.href}
        aria-current={customActive ? "page" : undefined}
        className={cn(className, customActive && "active")}
        {...props}
      >
        {icon}
        {label}
        {trailing}
      </SidebarAnchorPlain>
    );
  }

  /* Do not pass a function className — styled-components won't forward it to NavLink correctly. */
  return (
    <SidebarAnchorNav
      to={link.href}
      end={Boolean(link.end)}
      className={className}
      {...props}
    >
      {icon}
      {label}
      {trailing}
    </SidebarAnchorNav>
  );
};

/** Same layout/hover as SidebarLink, for buttons (e.g. logout). */
export const SidebarAction = ({ label, icon, className, type = "button", ...props }) => {
  const { open, animate } = useSidebar();
  return (
    <SidebarActionEl type={type} className={cn(className)} {...props}>
      {icon}
      <motion.span
        animate={{
          display: animate ? (open ? "inline-block" : "none") : "inline-block",
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        style={{
          fontSize: "0.875rem",
          whiteSpace: "nowrap",
          color: "inherit",
          transition: "transform 150ms",
        }}
      >
        {label}
      </motion.span>
    </SidebarActionEl>
  );
};

